import { addDays, format, parseISO } from "date-fns"
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { Alert, Button, Card, Container, Form } from "react-bootstrap"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import api from "../api/api"
import { useAlert } from "../context/AlertContext"
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  DAYS_OF_WEEK,
  DAY_ORDER,
  MAX_IMAGE_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE
} from "../constants"
import authService from "../services/authService"
import { logger } from "../utils/logger"
import {
  getMediaSelectionError,
  getUploadErrorMessage,
} from "../utils/mediaUploadErrors"
import {
  WALKTHROUGH_FORM_PROGRESS_EVENT,
  WALKTHROUGH_HISTORY_STATE_KEY,
} from "../utils/walkthroughEvents"
import { FormDirtyContext } from "./BottomNavBar"
import MediaPicker from "./MediaPicker"
import "../css/ProgramForm.css"

const today = format(new Date(), "yyyy-MM-dd")
const WALKTHROUGH_SCHEDULE_SETTLE_MS = 5000
let nextLocalExerciseId = 0

const createEmptyExercise = () => ({
  localId: nextLocalExerciseId++,
  name: "",
  instructions: "",
  image: null,
  video: null,
  existingImage: null,
  existingVideo: null,
  removeExistingImage: false,
  removeExistingVideo: false,
  pendingDelete: false,
})

const resizeInstructionsTextarea = (textarea) => {
  if (!textarea) return

  textarea.style.height = "auto"
  textarea.style.height = `${textarea.scrollHeight}px`
}

const useNavigationProtection = (isDirty) => {
  const [showDialog, setShowDialog] = useState(false)
  const [nextLocation, setNextLocation] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault()
        event.returnValue = ""
        return ""
      }
    }

    const handleLocationChange = () => {
      if (isDirty) {
        setShowDialog(true)
        setNextLocation(window.location.pathname)
        window.history.pushState(null, "", window.location.pathname)
        return false
      }
      return true
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("popstate", handleLocationChange)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("popstate", handleLocationChange)
    }
  }, [isDirty])

  const handleConfirmNavigation = () => {
    setShowDialog(false)
    if (nextLocation) {
      navigate(nextLocation)
      setNextLocation(null)
    }
  }

  const handleCancelNavigation = () => {
    setShowDialog(false)
    setNextLocation(null)
  }

  const checkNavigation = (to) => {
    if (isDirty) {
      setShowDialog(true)
      setNextLocation(to)
      return false
    }
    return true
  }

  return {
    showDialog,
    handleConfirmNavigation,
    handleCancelNavigation,
    checkNavigation
  }
}

const ProgramForm = () => {
  const { clientId, programId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const path = location.pathname
  const isWalkthroughActive = Boolean(
    location.state?.[WALKTHROUGH_HISTORY_STATE_KEY]?.key
  )
  const isEditMode = path.includes("/program/edit/")
  const isDuplicateMode = path.includes("/program/duplicate/")

  const [formData, setFormData] = useState({
    name: "",
    start_date: today,
    end_date: "",
    days_of_week: []
  })
  const [exercises, setExercises] = useState(() =>
    isEditMode || isDuplicateMode ? [] : [createEmptyExercise()]
  )
  const [confirmedEndDate, setConfirmedEndDate] = useState("")
  const minimumEndDate = formData.start_date
    ? format(addDays(parseISO(formData.start_date), 1), "yyyy-MM-dd")
    : undefined
  const hasValidEndDate = Boolean(
    formData.end_date && formData.start_date && formData.end_date > formData.start_date
  )

  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState({})
  const [mediaErrors, setMediaErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [clientDetails, setClientDetails] = useState(null)
  const [successMessage, setSuccessMessage] = useState("")
  const exerciseRefs = useRef([])
  const daysOfWeekRef = useRef(null)
  const endDateRef = useRef(null)

  const { setIsDirty: setIsDirtyContext, setChanges: setChangesContext } = useContext(FormDirtyContext)

  const [isDirty, setIsDirty] = useState(false)
  const [autoSaving, setAutoSaving] = useState(false)
  const [isProgramNameFocused, setIsProgramNameFocused] = useState(false)
  const [focusedExerciseNameIndex, setFocusedExerciseNameIndex] = useState(null)
  const [focusedExerciseInstructionsIndex, setFocusedExerciseInstructionsIndex] =
    useState(null)
  const [scheduleSelectionDelayElapsed, setScheduleSelectionDelayElapsed] =
    useState(true)

  const programNameRef = useRef(null)
  const scheduleSelectionStartedRef = useRef(false)
  const scheduleAdvanceTimerRef = useRef(null)

  const initialSnapshotRef = useRef(null)
  const baselineSetRef = useRef(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const activeExercises = exercises.filter((exercise) => !exercise.pendingDelete)
    const hasFile = (value) =>
      typeof File !== "undefined" && value instanceof File

    const progress = {
      programNamed:
        Boolean((formData.name || "").trim()) && !isProgramNameFocused,
      // Unlike exerciseNamed, this remains true while the field has focus.
      // Next cue must require real, non-whitespace text, not merely a click.
      exerciseNameHasText: Boolean((activeExercises[0]?.name || "").trim()),
      exerciseNamed: Boolean((activeExercises[0]?.name || "").trim()) &&
        focusedExerciseNameIndex !== exercises.indexOf(activeExercises[0]),
      exerciseAdded: exercises.some(
        (exercise, index) =>
          !exercise.pendingDelete &&
          Boolean((exercise.name || "").trim()) &&
          Boolean((exercise.instructions || "").trim()) &&
          focusedExerciseNameIndex !== index &&
          focusedExerciseInstructionsIndex !== index
      ),
      mediaAdded: activeExercises.some(
        (exercise) =>
          hasFile(exercise.image) ||
          hasFile(exercise.video) ||
          Boolean(
            exercise.existingImage && !exercise.removeExistingImage
          ) ||
          Boolean(
            exercise.existingVideo && !exercise.removeExistingVideo
          )
      ),
      endDateSet: hasValidEndDate &&
        (!isWalkthroughActive || confirmedEndDate === formData.end_date),
      scheduleSet: Boolean(
        Array.isArray(formData.days_of_week) &&
          formData.days_of_week.length > 0 &&
          scheduleSelectionDelayElapsed
      ),
    }

    window.dispatchEvent(
      new CustomEvent(WALKTHROUGH_FORM_PROGRESS_EVENT, {
        detail: progress,
      })
    )
  }, [
    formData,
    exercises,
    isProgramNameFocused,
    focusedExerciseNameIndex,
    focusedExerciseInstructionsIndex,
    scheduleSelectionDelayElapsed,
    hasValidEndDate,
    isWalkthroughActive,
    confirmedEndDate,
  ])

  const snapshotCurrent = useCallback(() => {
    return {
      formData: {
        name: formData.name || "",
        start_date: formData.start_date || "",
        end_date: formData.end_date || "",
        days_of_week: Array.isArray(formData.days_of_week) ? [...formData.days_of_week].sort() : []
      },
      exercises: (exercises || []).map((ex) => ({
        id: ex.id || null,
        name: ex.name || "",
        instructions: ex.instructions || "",
        keepExistingImage: ex.keepExistingImage ?? null,
        keepExistingVideo: ex.keepExistingVideo ?? null,
        removeExistingImage: !!ex.removeExistingImage,
        removeExistingVideo: !!ex.removeExistingVideo,
        existingImage: ex.existingImage ?? null,
        existingVideo: ex.existingVideo ?? null,
        hasNewImage: ex.image instanceof File,
        hasNewVideo: ex.video instanceof File,
        pendingDelete: !!ex.pendingDelete
      }))
    }
  }, [formData, exercises])

  const buildChangeDescriptions = useCallback(() => {
    const initial = initialSnapshotRef.current
    if (!initial) return []

    const current = snapshotCurrent()
    const out = []

    const a = initial.formData
    const b = current.formData

    if (a.name !== b.name) out.push("Program name changed")
    if (a.start_date !== b.start_date) out.push("Start date changed")
    if (a.end_date !== b.end_date) out.push("End date changed")

    if ((a.days_of_week || []).join(",") !== (b.days_of_week || []).join(",")) {
      out.push("Days of week changed")
    }

    const byId = (arr) => {
      const m = new Map()
      arr.forEach((x, idx) => {
        const key = x.id ? `id:${x.id}` : `idx:${idx}`
        m.set(key, x)
      })
      return m
    }

    const ia = byId(initial.exercises)
    const ib = byId(current.exercises)

    for (const [key, exA] of ia.entries()) {
      const exB = ib.get(key)

      const label = exA.name || exB?.name || "Activity"

      // Truly removed from state (rare now, but keep it)
      if (!exB) {
        out.push(`Activity removed: ${label}`)
        continue
      }

      if (!exA.pendingDelete && exB.pendingDelete) {
        out.push(`Activity marked for removal: ${label}`)
      }

      if (exA.pendingDelete && !exB.pendingDelete) {
        out.push(`Activity restored: ${label}`)
      }

      // Other field-level changes only matter if not pending delete
      if (!exB.pendingDelete) {
        if (exA.name !== exB.name) out.push(`${label}: name changed`)
        if (exA.instructions !== exB.instructions) out.push(`${label}: instructions changed`)

        if (exB.hasNewImage && !exA.hasNewImage) {
          out.push(`${label}: new image selected`)
        }

        if (exB.hasNewVideo && !exA.hasNewVideo) {
          out.push(`${label}: new video selected`)
        }

        if (!exA.removeExistingImage && exB.removeExistingImage) {
          out.push(`${label}: image marked for removal`)
        }

        if (exA.removeExistingImage && !exB.removeExistingImage) {
          out.push(`${label}: image removal undone`)
        }

        if (!exA.removeExistingVideo && exB.removeExistingVideo) {
          out.push(`${label}: video marked for removal`)
        }

        if (exA.removeExistingVideo && !exB.removeExistingVideo) {
          out.push(`${label}: video removal undone`)
        }
      }
    }

    for (const [key, exB] of ib.entries()) {
      if (!ia.has(key)) {
        out.push(`Activity added${exB.name ? `: ${exB.name}` : ""}`)
      }
    }

    return Array.from(new Set(out))
  }, [snapshotCurrent])

  useEffect(() => {
    return () => {
      setIsDirtyContext(false)
      setChangesContext([])
    }
  }, [setIsDirtyContext, setChangesContext])

  useEffect(() => {
    return () => {
      if (scheduleAdvanceTimerRef.current) {
        window.clearTimeout(scheduleAdvanceTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isDirty) {
      setChangesContext([])
      return
    }
    const changes = buildChangeDescriptions()
    setChangesContext(changes)
  }, [isDirty, formData, exercises, buildChangeDescriptions, setChangesContext])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        const clientResponse = await api.get(`/users/client_details/?id=${clientId}`)
        if (cancelled) return
        setClientDetails(clientResponse.data)

        if (isEditMode || isDuplicateMode) {
          const programResponse = await api.get(`/programs/${programId}/`)
          if (cancelled) return
          const programData = programResponse.data

          const nextFormData =
            isEditMode
              ? {
                  name: programData.name,
                  start_date: programData.start_date,
                  end_date: programData.end_date,
                  days_of_week: programData.days_of_week
                }
              : {
                  name: `${programData.name} (Copy)`,
                  start_date: today,
                  end_date: "",
                  days_of_week: programData.days_of_week
                }

          const nextExercises = [...programData.exercises]
            .sort((a, b) => a.index - b.index)
            .map((exercise) => ({
              id: exercise.id,
              name: exercise.name,
              instructions: exercise.instructions || "",
              image: null,
              video: null,
              existingImage: exercise.image_url,
              existingVideo: exercise.video_url,
              keepExistingImage: true,
              keepExistingVideo: true,
              removeExistingImage: false,
              removeExistingVideo: false,
              sourceExerciseId: exercise.id,
              pendingDelete: false,
            }))

          setFormData(nextFormData)
          setExercises(nextExercises)

          if (!baselineSetRef.current) {
            baselineSetRef.current = true
            initialSnapshotRef.current = {
              formData: {
                name: nextFormData.name || "",
                start_date: nextFormData.start_date || "",
                end_date: nextFormData.end_date || "",
                days_of_week: Array.isArray(nextFormData.days_of_week)
                  ? [...nextFormData.days_of_week].sort()
                  : []
              },
              exercises: nextExercises.map((ex) => ({
                id: ex.id || null,
                name: ex.name || "",
                instructions: ex.instructions || "",
                keepExistingImage: ex.keepExistingImage ?? null,
                keepExistingVideo: ex.keepExistingVideo ?? null,
                removeExistingImage: false,
                removeExistingVideo: false,
                existingImage: ex.existingImage ?? null,
                existingVideo: ex.existingVideo ?? null,
                hasNewImage: false,
                hasNewVideo: false,
                pendingDelete: false,
              }))
            }
            setChangesContext([])
          }
        } else {
          const initialExercises = [createEmptyExercise()]

          if (!baselineSetRef.current) {
            baselineSetRef.current = true
            initialSnapshotRef.current = {
              formData: {
                name: "",
                start_date: today,
                end_date: "",
                days_of_week: []
              },
              exercises: initialExercises.map((ex) => ({
                id: null,
                name: ex.name,
                instructions: ex.instructions,
                keepExistingImage: null,
                keepExistingVideo: null,
                removeExistingImage: false,
                removeExistingVideo: false,
                existingImage: null,
                existingVideo: null,
                hasNewImage: false,
                hasNewVideo: false,
                pendingDelete: false,
              }))
            }
            setChangesContext([])
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [clientId, programId, isEditMode, isDuplicateMode, setChangesContext])

  const createProgramFormData = useCallback(
    (isDraft) => {
      const programFormData = new FormData()
      const sortedDays = [...formData.days_of_week].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b])

      programFormData.append("client_id", clientId)
      programFormData.append("name", formData.name)
      programFormData.append("start_date", formData.start_date)
      programFormData.append("end_date", formData.end_date)
      programFormData.append("is_draft", isDraft)
      programFormData.append("days_of_week", sortedDays)

      return programFormData
    },
    [formData, clientId]
  )

  const createProgram = useCallback(async (programFormData) => {
    try {
      const programResponse = await api.post("/programs/", programFormData)
      return programResponse.data.id
    } catch (error) {
      const errorMessage = getHumanReadableError(error, "program creation")
      throw new Error(`Failed to create program: ${errorMessage}`, {
        cause: error
      })
    }
  }, [])

  const createExercisesFormData = useCallback(
    (programId) => {
      const exercisesFormData = new FormData()
      exercisesFormData.append("program_id", programId)

      const exercisesData = exercises.map((exercise) => ({
        name: exercise.name,
        instructions: exercise.instructions || "",
        keepExistingImage: exercise.keepExistingImage,
        keepExistingVideo: exercise.keepExistingVideo,
        sourceExerciseId: exercise.sourceExerciseId,
        id: exercise.id || null,
        existingImage: exercise.existingImage,
        existingVideo: exercise.existingVideo,
      }))

      exercisesFormData.append("exercises", JSON.stringify(exercisesData))

      exercises.forEach((exercise, index) => {
        if (exercise.image) {
          exercisesFormData.append(`exercise_${index}_image`, exercise.image)
        }
        if (exercise.video) {
          exercisesFormData.append(`exercise_${index}_video`, exercise.video)
        }
      })

      return exercisesFormData
    },
    [exercises]
  )

  const createExercises = useCallback(async (exercisesFormData) => {
    try {
      return await api.post("/exercises/batch_create/", exercisesFormData)
    } catch (error) {
      logger.error("Failed to create exercises", {
        url: "/exercises/batch_create/",
        exerciseFormData: exercisesFormData
      })
      // Preserve the original Axios error so network/status details remain
      // available for the user-facing upload error formatter.
      throw error
    }
  }, [])

  const markProgramReplicated = useCallback(
    async (createdProgramId) => {
      if (!isDuplicateMode || !programId || !createdProgramId) return

      try {
        await api.post(`/programs/${createdProgramId}/mark-replicated/`, {
          source_program_id: programId
        })
      } catch (error) {
        logger.error("Unable to record program replication", {
          cause: error,
          url: `/programs/${createdProgramId}/mark-replicated/`
        })
      }
    },
    [isDuplicateMode, programId]
  )

  const handleExerciseCreationError = useCallback(async (error, programId) => {
    try {
      await api.delete(`/programs/${programId}/`)
    } catch (deleteError) {
      const cleanupError = new Error(
        `Failed to clean up program after exercise creation error. Program ID: ${programId}. Original error: ${error.message}`,
        { cause: deleteError }
      )
      logger.error("Cleanup failed:", {
        cause: cleanupError,
        url: "/programs/"
      })
      throw cleanupError
    }

    const errorMessage = getHumanReadableError(error, "activity creation")
    logger.error(`Exercise creation failed: ${errorMessage}`, {
      cause: error,
      url: "/exercises/batch_create/"
    })
    throw new Error(`Activity creation failed: ${errorMessage}`, {
      cause: error
    })
  }, [])

  const hasPendingMediaUploads = useCallback(
    () =>
      exercises.some(
        (exercise) =>
          exercise.image instanceof File || exercise.video instanceof File
      ),
    [exercises]
  )

  const handleSuccess = useCallback(
    (exerciseResponse, isDraft) => {
      if (isDraft) {
        if (exerciseResponse.data.user_message) {
          setSuccessMessage(exerciseResponse.data.user_message)
        } else {
          setSuccessMessage("Program saved as draft")
        }
      } else {
        showAlert("success", "Program successfully sent to client")
      }

      setIsDirty(false)
      setIsDirtyContext(false)
      setChangesContext([])
      setExercises((prev) => prev.filter((ex) => !ex.pendingDelete))

      initialSnapshotRef.current = snapshotCurrent()
    },
    [setIsDirtyContext, setChangesContext, showAlert, snapshotCurrent]
  )

  const handleSubmissionError = useCallback(
    (error) => {
      const errorMessage = hasPendingMediaUploads()
        ? getUploadErrorMessage(error, "form submission")
        : getHumanReadableError(error, "form submission")
      setError(errorMessage)

      const debugInfo = {
        formData: JSON.stringify(formData),
        clientId,
        userEmail: localStorage.getItem("user_email"),
        errorDetails: error.message
      }
      logger.error("Submission error:", {
        cause: error,
        url: "/programs/",
        debugInfo
      })
    },
    [formData, clientId, hasPendingMediaUploads]
  )

  useEffect(() => {
    authService.refresh()

    const saveBeforeLogout = (event) => {
      const savePromise = (async () => {
        if (!isDirty) return

        try {
          setAutoSaving(true)
          setSuccessMessage("Saving draft before logout...")

          const programFormData = await createProgramFormData(true)
          const createdProgramId = await createProgram(programFormData)

          if (createdProgramId) {
            const exercisesFormData = await createExercisesFormData(createdProgramId)
            await createExercises(exercisesFormData)
            await markProgramReplicated(createdProgramId)
          }

          setSuccessMessage("Draft saved successfully before logout")
          setIsDirty(false)
          setIsDirtyContext(false)
          setChangesContext([])

          // Keep the saved draft as the new baseline so the walkthrough can
          // resume from server-backed program state after the next login.
          initialSnapshotRef.current = snapshotCurrent()
        } catch (error) {
          logger.error("Failed to save draft before logout:", {
            cause: error,
            url: "/programs/"
          })
          setError("Failed to save draft before logout")
        } finally {
          setAutoSaving(false)
        }
      })()

      event.detail?.waitUntil?.(savePromise)
    }

    window.addEventListener("beforeLogout", saveBeforeLogout)

    return () => {
      window.removeEventListener("beforeLogout", saveBeforeLogout)
    }
  }, [
    isDirty,
    isEditMode,
    isDuplicateMode,
    createProgramFormData,
    createProgram,
    createExercisesFormData,
    createExercises,
    markProgramReplicated,
    setIsDirtyContext,
    setChangesContext,
    snapshotCurrent
  ])

  const setMediaFieldError = (index, field, message) => {
    setMediaErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: message,
      },
    }))
  }

  const clearMediaFieldError = (index, field) => {
    setMediaErrors((prev) => {
      if (!prev[index]?.[field]) return prev

      const next = { ...prev }
      const nextExerciseErrors = { ...next[index] }
      delete nextExerciseErrors[field]

      if (Object.keys(nextExerciseErrors).length === 0) {
        delete next[index]
      } else {
        next[index] = nextExerciseErrors
      }

      return next
    })
  }

  const normalizeExerciseName = (name) => {
    return name
      ? name
          .trim()
          .replace(/\s+/g, " ")
          .toLowerCase()
      : ""
  }

  const handleExerciseChange = (index, field, value) => {
    setExercises((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        [field]: value
      }

      if (field === "name") {
        const normalizedNewValue = normalizeExerciseName(value)
        const hasDuplicate =
          normalizedNewValue &&
          updated.some((exercise, i) => i !== index && normalizeExerciseName(exercise.name) === normalizedNewValue)

        if (hasDuplicate) {
          setFieldErrors((prev) => ({
            ...prev,
            exercises: {
              ...prev?.exercises,
              [index]: {
                ...prev?.exercises?.[index],
                name: "Activity name has already been used in this program"
              }
            }
          }))
        } else {
          setFieldErrors((prev) => {
            const updatedErr = { ...prev }
            if (updatedErr.exercises?.[index]?.name) {
              delete updatedErr.exercises[index].name
              if (Object.keys(updatedErr.exercises[index]).length === 0) {
                delete updatedErr.exercises[index]
              }
              if (Object.keys(updatedErr.exercises).length === 0) {
                delete updatedErr.exercises
              }
            }
            return updatedErr
          })
        }
      }

      return updated
    })
    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const handleFileChange = async (index, field, file) => {
    if (!file) return

    const isImage = field === "image"
    const kind = isImage ? "image" : "video"
    const allowedTypes = isImage ? ALLOWED_IMAGE_TYPES : ALLOWED_VIDEO_TYPES
    const maxSizeBytes = isImage ? MAX_IMAGE_FILE_SIZE : MAX_VIDEO_FILE_SIZE
    const selectionError = getMediaSelectionError({
      file,
      kind,
      allowedTypes,
      maxSizeBytes,
    })

    if (selectionError) {
      setMediaFieldError(index, field, selectionError)
      return
    }

    clearMediaFieldError(index, field)

    const suffix = isImage ? "Image" : "Video"

    setExercises((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        [field]: file,
        [`keepExisting${suffix}`]: false,
        [`removeExisting${suffix}`]: false
      }
      return updated
    })

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const handleMediaClear = (index, field) => {
    clearMediaFieldError(index, field)

    const isImage = field === "image"
    const suffix = isImage ? "Image" : "Video"
    const existingField = isImage ? "existingImage" : "existingVideo"

    setExercises((prev) => {
      const updated = [...prev]
      const exercise = updated[index]
      const hasNewFile = exercise?.[field] instanceof File
      const hasExistingFile = !!exercise?.[existingField]

      if (hasNewFile) {
        updated[index] = {
          ...exercise,
          [field]: null,
          [`keepExisting${suffix}`]: hasExistingFile,
          [`removeExisting${suffix}`]: false
        }
      } else if (hasExistingFile) {
        updated[index] = {
          ...exercise,
          [`keepExisting${suffix}`]: false,
          [`removeExisting${suffix}`]: true
        }
      }

      return updated
    })

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const handleMediaRestore = (index, field) => {
    clearMediaFieldError(index, field)

    const isImage = field === "image"
    const suffix = isImage ? "Image" : "Video"

    setExercises((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        [`keepExisting${suffix}`]: true,
        [`removeExisting${suffix}`]: false
      }
      return updated
    })

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const addExercise = () => {
    setExercises((prev) => [
      ...prev,
      createEmptyExercise()
    ])

    setTimeout(() => {
      const newIndex = exerciseRefs.current.length - 1
      const inputElement = exerciseRefs.current[newIndex]?.querySelector('input[type="text"]')
      if (inputElement) {
        inputElement.focus()
      }
    }, 0)

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const removeExercise = (index) => {
    setMediaErrors({})

    setExercises((prev) => {
      const updated = [...prev]
      const ex = updated[index]

      if (ex?.id) {
        updated[index] = {
          ...ex,
          pendingDelete: true
        }
        return updated
      }

      return updated.filter((_, i) => i !== index)
    })

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const undoRemoveExercise = (index) => {
    setExercises((prev) => {
      const updated = [...prev]
      const ex = updated[index]
      if (!ex) return prev
      updated[index] = {
        ...ex,
        pendingDelete: false
      }
      return updated
    })

    setIsDirty(true)
    setIsDirtyContext(true)
  }

  const handleDayToggle = (day) => {
    const isFirstScheduleSelection =
      !scheduleSelectionStartedRef.current &&
      formData.days_of_week.length === 0 &&
      !formData.days_of_week.includes(day)

    if (isFirstScheduleSelection) {
      scheduleSelectionStartedRef.current = true
      setScheduleSelectionDelayElapsed(false)

      scheduleAdvanceTimerRef.current = window.setTimeout(() => {
        setScheduleSelectionDelayElapsed(true)
        scheduleAdvanceTimerRef.current = null
      }, WALKTHROUGH_SCHEDULE_SETTLE_MS)
    }

    setIsDirty(true)
    setIsDirtyContext(true)
    setFormData((prev) => {
      const currentDays = prev.days_of_week
      if (currentDays.includes(day)) {
        return {
          ...prev,
          days_of_week: currentDays.filter((d) => d !== day)
        }
      } else {
        if (fieldErrors.days_of_week && currentDays.length === 0) {
          setFieldErrors((prev) => ({
            ...prev,
            days_of_week: undefined
          }))
        }
        return {
          ...prev,
          days_of_week: [...currentDays, day]
        }
      }
    })
  }

  const isProgramComplete = () => {
    const isProgramComplete =
      formData.name &&
      formData.start_date &&
      formData.end_date &&
      formData.days_of_week.length > 0

    const areExercisesComplete =
      exercises.length > 0 &&
      exercises.every((ex) => ex.pendingDelete || ex.name?.trim())

    return isProgramComplete && areExercisesComplete
  }

  const validateForm = () => {
    const errors = {}
    let isValid = true
    let firstErrorElement = null

    if (!formData.name.trim()) {
      errors.name = "Program name is required"
      isValid = false
      firstErrorElement = programNameRef.current
    }

    const exerciseErrors = {}
    for (let index = 0; index < exercises.length; index++) {
      const exercise = exercises[index]
      const exerciseError = {}

      if (!exercise.name.trim()) {
        exerciseError.name = "Activity name is required"
        isValid = false
        firstErrorElement = firstErrorElement || exerciseRefs.current[index]
      }

      if (Object.keys(exerciseError).length > 0) {
        exerciseErrors[index] = exerciseError
      }
    }

    if (Object.keys(exerciseErrors).length > 0) {
      errors.exercises = exerciseErrors
    }

    if (!formData.end_date) {
      errors.end_date = "End date is required"
      firstErrorElement = firstErrorElement || endDateRef.current
      isValid = false
    } else if (formData.end_date <= formData.start_date) {
      errors.end_date = "End date must be after start date"
      firstErrorElement = firstErrorElement || endDateRef.current
      isValid = false
    }

    if (formData.days_of_week.length === 0) {
      errors.days_of_week = "Please select at least one day of the week"
      firstErrorElement = firstErrorElement || daysOfWeekRef.current
      isValid = false
    }

    setFieldErrors(errors)

    if (firstErrorElement) {
      setTimeout(() => {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "start"
        })
      }, 100)
    }

    return isValid
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "end_date" || name === "start_date") setConfirmedEndDate("")
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))
    setIsDirty(true)
    setIsDirtyContext(true)

    setFieldErrors((prev) => {
      const updated = { ...prev }
      delete updated[name]
      return updated
    })

    if (
      name === "end_date" &&
      value &&
      formData.start_date &&
      value <= formData.start_date
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        end_date: "End date must be after start date"
      }))
    }
  }

  const handleSubmit = async (isDraft = true) => {
    setError("")
    setSuccessMessage("")
    setLoading(true)

    try {
      if (!isDraft && !validateForm()) {
        setLoading(false)
        return
      }

      if (!isDraft && !isProgramComplete()) {
        if (!isProgramComplete()) console.log("Program is incomplete")
        if (!isDraft) console.log("Not saving as draft")
        setError("Please complete all required fields before sending to the client")
        setLoading(false)
        return
      }

      const programFormData = createProgramFormData(isDraft)
      let createdProgramId

      if (isEditMode) {
        await api.put(`/programs/${programId}/`, programFormData)
        createdProgramId = programId

        const exercisesToDelete = exercises.filter((ex) => ex.id && ex.pendingDelete)
        const exercisesToUpsert = exercises.filter((ex) => !ex.pendingDelete)

        for (const exercise of exercisesToUpsert) {
          const exerciseFormData = new FormData()
          exerciseFormData.append("name", exercise.name)
          exerciseFormData.append("instructions", exercise.instructions || "")
          exerciseFormData.append("program_id", programId)

          if (exercise.image instanceof File) {
            exerciseFormData.append("image", exercise.image)
          }

          if (exercise.video instanceof File) {
            exerciseFormData.append("video", exercise.video)
          }

          if (exercise.removeExistingImage) {
            exerciseFormData.append("remove_image", "true")
          }

          if (exercise.removeExistingVideo) {
            exerciseFormData.append("remove_video", "true")
          }

          if (exercise.id) {
            await api.put(`/exercises/${exercise.id}/`, exerciseFormData)
          } else {
            const response = await api.post("/exercises/", exerciseFormData)
            // A later upload or publication can fail after this create commits.
            // Retain its ID immediately so retrying updates the saved activity.
            setExercises((prev) => prev.map((current) =>
              current.localId === exercise.localId
                ? { ...current, id: response.data.id }
                : current
            ))
          }
        }

        for (const exercise of exercisesToDelete) {
          await api.delete(`/exercises/${exercise.id}/`)
        }

        if (!isDraft) {
          await api.post(`/program-versions/create_version/`, {
            program: programId,
            change_reason: "Program updated"
          })
        }

        handleSuccess(
          {
            data: {
              user_message: isDraft
                ? "Draft updated"
                : "Program updated and sent to client"
            }
          },
          isDraft
        )
      } else {
        createdProgramId = await createProgram(programFormData)

        try {
          const exercisesFormData = createExercisesFormData(createdProgramId)
          const exerciseResponse = await createExercises(exercisesFormData)
          await markProgramReplicated(createdProgramId)

          if (!isDraft) {
            await api.post(`/program-versions/create_version/`, {
              program: createdProgramId,
              change_reason: "Initial program creation"
            })
          }

          handleSuccess(exerciseResponse, isDraft)
        } catch (exerciseError) {
          await handleExerciseCreationError(exerciseError, createdProgramId)
        }
      }

      setIsDirty(false)
      setIsDirtyContext(false)
      setChangesContext([])
      initialSnapshotRef.current = snapshotCurrent()

      setTimeout(() => {
        const today = format(new Date(), "yyyy-MM-dd")
        navigate(`/program/${createdProgramId}/?date=${today}`)
      }, 1500)
    } catch (error) {
      handleSubmissionError(error)
    } finally {
      setLoading(false)
    }
  }

  const { showDialog, handleConfirmNavigation, handleCancelNavigation } =
    useNavigationProtection(isDirty && !isWalkthroughActive)

  const getHumanReadableError = (error, context) => getUploadErrorMessage(error, context)

  return (
    <Container className="program-form-page d-flex justify-content-center align-items-center py-3 mb-5">
      <div className="program-form-row row w-100 justify-content-center">
        <div className="program-form-column col-12 col-md-8">
          <Card.Body className="program-form-body">
            <div className="program-form-header text-center p-4">
              <div className="d-flex flex-column align-items-center">
                <h2 className="fw-bold mb-3">
                  {isEditMode
                    ? "Edit Program"
                    : isDuplicateMode
                    ? "Replicate Program"
                    : "Create Program"}
                </h2>

                {clientDetails && (
                  <h4 className="text-secondary">
                    {clientDetails.client_profile?.is_practice
                      ? "Julia Smith"
                      : `${clientDetails.last_name}, ${clientDetails.first_name}`}
                  </h4>
                )}
              </div>
            </div>

            <Form onSubmit={(e) => e.preventDefault()}>
              <Form.Group
                className="mb-3"
                ref={programNameRef}
                data-walkthrough="program-name"
              >
                <Form.Label>
                  Program Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setIsProgramNameFocused(true)}
                  onBlur={() => setIsProgramNameFocused(false)}
                  required
                  placeholder="Enter program name"
                  isInvalid={!!fieldErrors.name}
                  className={fieldErrors.name ? "border-danger" : ""}
                />
                {fieldErrors.name && (
                  <Form.Text className="text-danger">
                    {fieldErrors.name}
                  </Form.Text>
                )}
              </Form.Group>

              <div className="exercises-section mt-4">
                <h4 className="mb-3">Activities</h4>

                {exercises.map((exercise, index) => {
                  const isRemoved = !!exercise.pendingDelete

                  return (
                    <Card
                      key={index}
                      className="program-exercise-card mb-4 p-3"
                      data-walkthrough={index === 0 ? "exercise" : undefined}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0">Activity {index + 1}</h5>
                          {isRemoved && (
                            <span
                              className="badge bg-secondary"
                              style={{ fontWeight: 600 }}
                            >
                              Will be removed on save
                            </span>
                          )}
                        </div>

                        {!isRemoved ? (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeExercise(index)}
                          >
                            Remove Activity
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            style={{ backgroundColor: "#5bba6f", borderColor: "#5bba6f" }}
                            onClick={() => undoRemoveExercise(index)}
                          >
                            Restore Activity
                          </Button>
                        )}
                      </div>

                      <fieldset disabled={isRemoved}
                      style={{
                        opacity: isRemoved ? 0.55 : 1,
                        filter: isRemoved ? "grayscale(0.25)" : "none"
                      }}>
                        <Form.Group
                          className="mb-3"
                          ref={(el) => (exerciseRefs.current[index] = el)}
                          data-walkthrough={
                            index === 0 ? "exercise-name" : undefined
                          }
                        >
                          <Form.Label>
                            Activity Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={exercise.name}
                            onChange={(e) =>
                              handleExerciseChange(index, "name", e.target.value)
                            }
                            onFocus={() => setFocusedExerciseNameIndex(index)}
                            onBlur={() => setFocusedExerciseNameIndex(null)}
                            required
                            placeholder="Enter activity name"
                            isInvalid={!!fieldErrors.exercises?.[index]?.name}
                          />
                          {fieldErrors.exercises?.[index]?.name && (
                            <Form.Text className="text-danger">
                              {fieldErrors.exercises[index].name}
                            </Form.Text>
                          )}
                        </Form.Group>

                        <Form.Group
                          className="mb-3"
                          data-walkthrough={index === 0 ? "exercise-instructions" : undefined}
                        >
                          <Form.Label>Instructions (Optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            ref={resizeInstructionsTextarea}
                            value={exercise.instructions || ""}
                            onChange={(e) => {
                              resizeInstructionsTextarea(e.target)
                              handleExerciseChange(
                                index,
                                "instructions",
                                e.target.value
                              )
                            }}
                            onFocus={() =>
                              setFocusedExerciseInstructionsIndex(index)
                            }
                            onBlur={() =>
                              setFocusedExerciseInstructionsIndex(null)
                            }
                            placeholder="Enter activity instructions if needed"
                            className="program-instructions-textarea"
                          />
                        </Form.Group>

                        <div
                          data-walkthrough={index === 0 ? "media" : undefined}
                        >
                          <Form.Group className="mb-3">
                            <Form.Label>Image (Optional)</Form.Label>
                            <MediaPicker
                              kind="image"
                              accept={ALLOWED_IMAGE_TYPES}
                              maxSizeBytes={MAX_IMAGE_FILE_SIZE}
                              disabled={isRemoved}
                              captureLabel="Take photo"
                              libraryLabel="Library"
                              valueFile={exercise.image instanceof File ? exercise.image : null}
                              valueUrl={exercise.existingImage || null}
                              removed={!!exercise.removeExistingImage}
                              onSelect={(file) => handleFileChange(index, "image", file)}
                              onClear={() => handleMediaClear(index, "image")}
                              onRestore={() => handleMediaRestore(index, "image")}
                              onError={(msg) => setMediaFieldError(index, "image", msg)}
                            />
                            {mediaErrors[index]?.image && (
                              <Form.Text className="text-danger d-block mt-2" role="alert">
                                {mediaErrors[index].image}
                              </Form.Text>
                            )}
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Video (Optional - Approx 60 second limit)</Form.Label>
                            <MediaPicker
                              kind="video"
                              accept={ALLOWED_VIDEO_TYPES}
                              maxSizeBytes={MAX_VIDEO_FILE_SIZE}
                              maxDurationSec={60}
                              disabled={isRemoved}
                              captureLabel="Record video"
                              libraryLabel="Library"
                              valueFile={exercise.video instanceof File ? exercise.video : null}
                              valueUrl={exercise.existingVideo || null}
                              removed={!!exercise.removeExistingVideo}
                              onSelect={(file) => handleFileChange(index, "video", file)}
                              onClear={() => handleMediaClear(index, "video")}
                              onRestore={() => handleMediaRestore(index, "video")}
                              onError={(msg) => setMediaFieldError(index, "video", msg)}
                            />
                            {mediaErrors[index]?.video && (
                              <Form.Text className="text-danger d-block mt-2" role="alert">
                                {mediaErrors[index].video}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </div>
                      </fieldset>
                    </Card>
                  )
                })}

                <Button
                  variant="secondary"
                  type="button"
                  onClick={addExercise}
                  className="w-100 mb-4"
                >
                  Add An Activity
                </Button>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group
                className="mb-3"
                ref={endDateRef}
                data-walkthrough="end-date"
              >
                <Form.Label htmlFor="program-end-date">
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  id="program-end-date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  min={minimumEndDate}
                  required
                  isInvalid={!!fieldErrors.end_date}
                />
                {isWalkthroughActive && (
                  <div className="mt-2">
                    <Form.Text className="d-block mb-2">
                      Choose an end date after the start date, then confirm to continue.
                    </Form.Text>
                    <Button type="button" variant="primary"
                      disabled={!hasValidEndDate}
                      onClick={() => setConfirmedEndDate(formData.end_date)}>
                      Confirm end date
                    </Button>
                  </div>
                )}
                {fieldErrors.end_date && (
                  <Form.Text className="text-danger">
                    {fieldErrors.end_date}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group
                className="mb-4"
                ref={daysOfWeekRef}
                data-walkthrough="schedule"
              >
                <Form.Label>
                  Days of Week <span className="text-danger">*</span>
                </Form.Label>
                <div className="program-days-grid">
                  {DAYS_OF_WEEK.map((day) => (
                    <Button
                      key={day.value}
                      variant={
                        formData.days_of_week.includes(day.value)
                          ? "primary"
                          : "outline-primary"
                      }
                      onClick={() => handleDayToggle(day.value)}
                      type="button"
                      className={`program-day-button ${
                        fieldErrors.days_of_week ? "border-danger" : ""
                      }`}
                    >
                      <span className="program-day-label-full">{day.label}</span>
                      <span className="program-day-label-short">{day.label.slice(0, 3)}</span>
                    </Button>
                  ))}
                </div>
                {fieldErrors.days_of_week && (
                  <Form.Text className="text-danger">
                    {fieldErrors.days_of_week}
                  </Form.Text>
                )}
              </Form.Group>

              {error && (
                <Alert variant="danger" className="mb-3 unmask-me" role="alert">
                  <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                    {error}
                  </pre>
                </Alert>
              )}

              {successMessage && (
                <Alert
                  variant="success"
                  className="mb-3"
                  role="status"
                  aria-live="polite"
                >
                  {successMessage}
                </Alert>
              )}

              <div className="d-flex gap-3 mb-5">
                <Button
                  variant="secondary"
                  onClick={() => handleSubmit(true)}
                  className=""
                  disabled={
                    loading ||
                    autoSaving ||
                    Object.values(fieldErrors?.exercises || {}).some((err) =>
                      err.name?.includes("already been used")
                    )
                  }
                >
                  {loading || autoSaving
                    ? "Saving..."
                    : isEditMode
                    ? "Save Changes"
                    : "Save as Draft"}
                </Button>

                <Button
                  data-walkthrough="publish"
                  onClick={() => handleSubmit(false)}
                  className="custom-pink-button flex-grow-1"
                  disabled={loading || autoSaving}
                >
                  {loading || autoSaving
                    ? "Saving..."
                    : "Send to client"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </div>
      </div>

      {showDialog && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Unsaved Changes</h5>
              </div>
              <div className="modal-body">
                <div className="mb-2">You have unsaved changes:</div>
                {(() => {
                  const changes = buildChangeDescriptions()
                  console.log("Changes detected:", changes)
                  if (!changes.length) return <div className="text-muted">Changes detected.</div>
                  return (
                    <ul className="mb-0">
                      {changes.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  )
                })()}
                <div className="mt-3">Are you sure you want to leave?</div>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={handleCancelNavigation}>
                  Stay on Page
                </Button>
                <Button variant="primary" onClick={handleConfirmNavigation}>
                  Leave Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  )
}

export default ProgramForm


